
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { getUserSurveyResponses, deleteSurveyResponse } from '@/services/surveyService';
import { BarChart2, Trash2, ExternalLink, Plus, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface SavedSurvey {
  id: string;
  project_name: string;
  industry: string;
  created_at: string;
  score_breakdown: any;
}

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [savedSurveys, setSavedSurveys] = useState<SavedSurvey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(true);

  useEffect(() => {
    fetchSavedSurveys();
  }, []);

  const fetchSavedSurveys = async () => {
    setIsLoading(true);
    try {
      const surveys = await getUserSurveyResponses();
      
      // Fix for duplicate entries: Create a Map using survey ID as the key
      // This will automatically remove duplicates as Map keys must be unique
      const uniqueSurveysMap = new Map();
      surveys.forEach(survey => {
        // Ensure score_breakdown exists
        if (!survey.score_breakdown) {
          survey.score_breakdown = {
            totalNEV: 0,
            eroi: 0,
            rating: 'High Risk'
          };
        }
        uniqueSurveysMap.set(survey.id, survey);
      });
      
      // Convert back to array and sort by creation date (newest first)
      const uniqueSurveys = Array.from(uniqueSurveysMap.values());
      uniqueSurveys.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      setSavedSurveys(uniqueSurveys);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast({
        title: t('dashboard.loadError'),
        description: t('dashboard.loadErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSurvey = async (id: string) => {
    if (!confirm(t('dashboard.confirmDelete'))) return;
    
    try {
      await deleteSurveyResponse(id);
      toast({
        title: t('dashboard.deleted'),
        description: t('dashboard.deletedDesc'),
      });
      fetchSavedSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast({
        title: t('dashboard.deleteError'),
        description: t('dashboard.deleteErrorDesc'),
        variant: "destructive",
      });
    }
  };

  const handleViewResults = (survey: SavedSurvey) => {
    // Store the complete survey object in sessionStorage for the Results page to access
    sessionStorage.setItem('selectedSurvey', JSON.stringify(survey));
    navigate('/results');
  };

  const isAdmin = user?.email && ['owner@example.com', 'admin@example.com'].includes(user.email);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Logo className="mx-auto mb-4" showVersion={true} />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="w-full flex justify-end mb-2">
          <LanguageSwitcher />
        </div>
        
        {/* Adding white box background to capture all content */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="mb-8 flex flex-col items-center">
            <Logo className="mb-4" showVersion={true} />
            <h1 className="text-3xl font-bold mb-2">{t('dashboard.title')}</h1>
            <p className="text-gray-500">{t('dashboard.subtitle')}</p>
          </div>
          
          {showHelp && (
            <Alert className="mb-6 bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-700">Dashboard Guide</AlertTitle>
              <AlertDescription className="text-blue-600">
                Welcome to your dashboard! Here you can see all your saved assessments, 
                create new ones, and review past results. Saving assessments allows you to 
                track your project's emotional value over time and compare different projects.
                <Button 
                  variant="link" 
                  className="text-blue-600 p-0 h-auto ml-2"
                  onClick={() => setShowHelp(false)}
                >
                  Dismiss
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t('dashboard.savedAssessments')}</h2>
            <div className="flex gap-2">
              {isAdmin && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/admin')}
                  className="flex items-center"
                >
                  <BarChart2 className="mr-2 h-4 w-4" /> Admin Dashboard
                </Button>
              )}
              <Button onClick={() => navigate('/industry')} className="flex items-center">
                <Plus className="mr-2 h-4 w-4" /> {t('dashboard.newAssessment')}
              </Button>
            </div>
          </div>

          {savedSurveys.length === 0 ? (
            <Card className="mb-8 text-center py-12">
              <CardContent>
                <p className="text-gray-500 mb-6">{t('dashboard.noSavedAssessments')}</p>
                <Button onClick={() => navigate('/industry')} className="flex items-center mx-auto">
                  <Plus className="mr-2 h-4 w-4" /> {t('dashboard.startFirst')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {savedSurveys.map((survey) => {
                // Make sure score_breakdown exists and has the necessary properties
                const scoreBreakdown = survey.score_breakdown || { totalNEV: 0, eroi: 0 };
                const totalNEV = typeof scoreBreakdown.totalNEV === 'number' ? scoreBreakdown.totalNEV : 0;
                const eroi = typeof scoreBreakdown.eroi === 'number' ? scoreBreakdown.eroi : 0;
                
                return (
                  <Card key={survey.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{survey.project_name}</CardTitle>
                      <CardDescription>{survey.industry}</CardDescription>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <div className="text-sm text-gray-500">{t('results.nev')}</div>
                          <div className={`text-lg font-bold ${
                            totalNEV < 30 ? 'text-red-500' : 
                            totalNEV < 45 ? 'text-amber-500' : 
                            'text-green-500'
                          }`}>
                            {totalNEV.toFixed(1)}/50
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">{t('results.eroi')}</div>
                          <div className={`text-lg font-bold ${
                            eroi < 1.0 ? 'text-red-500' : 
                            eroi < 2.0 ? 'text-amber-500' : 
                            'text-green-500'
                          }`}>
                            {eroi.toFixed(2)}x
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">{t('dashboard.date')}</div>
                          <div className="text-sm">
                            {new Date(survey.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-between">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteSurvey(survey.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> {t('dashboard.delete')}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewResults(survey)}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> {t('dashboard.view')}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="text-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              {t('nav.backToHome')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
